import * as H from 'history';
import { ZodType } from 'zod';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

type ChangeURLParamsFunc = (
  paramsObject: Record<string, string | string[] | null>
) => void;

/** Update browser's URL with new params. You can also delete a param by setting it to null
 * 
 * @param paramsObject
 * Pass an object where each key is the searchParam's name, and the value is a string specifying the value you want for that search param.
 * Pass null as the value to delete that searchParam from the URL.
 * Pass a string array as the value to add multiple searchParams to the URL, one for each value provided in the array.
 * 
 * @example
 * const changeURLParams = useChangeURLParams();
 * ...
 * changeURLParams({
 *      pageNo: "1",
 *      taskState: ['ToDo', 'InProgress'],
 *      annotationState: null
 * });
   // The above will update the URL to contain, in addition to any existing query params:
   // pageNo=1&taskState=ToDo&taskState=InProgress
   // ensuring that annotationState was removed from the URL.
 */
export const useChangeURLParams = (): ChangeURLParamsFunc => {
  const location = useLocation();
  const history = useHistory();

  return useCallback(
    (paramsObject: Record<string, string | string[] | null>) => {
      changeURLParams(history, location, paramsObject);
    },
    [history, location]
  );
};

export function changeURLParams(
  history: H.History<H.LocationState>,
  location: H.Location<H.LocationState>,
  paramsObject: Record<string, string | string[] | null>
): void {
  const params = new URLSearchParams(location.search);

  Object.entries(paramsObject).forEach(([key, value]) => {
    if (value === null) {
      params.delete(key);
    } else if (Array.isArray(value)) {
      params.delete(key);
      for (const v of value) {
        params.append(key, v);
      }
    } else {
      params.set(key, value);
    }
  });

  history.replace({
    pathname: location.pathname,
    search: `?${params.toString()}`,
  });
}

export const deleteURLParams = (
  history: H.History<H.LocationState>,
  location: H.Location<H.LocationState>,
  paramsToDelete: string[]
): void => {
  const newParams = new URLSearchParams(location.search);

  paramsToDelete.forEach((key) => {
    newParams.delete(key);
  });

  history.replace({
    pathname: location.pathname,
    search: `?${newParams.toString()}`,
  });
};

export function replaceHistorySearch(
  history: H.History<H.LocationState>,
  location: H.Location<H.LocationState>,
  newSearch: string
): void {
  history.replace({
    pathname: location.pathname,
    search: newSearch,
  });
}

/**
 *
 * @param queryParam Name of the query param: ?param_name_case_sensitive=
 * @param valueZodType Zod Schema that'll be used to parse the value in URL
 * @param defaultValue Default value if the value for this query_param is missing in URL
 * @returns Return of ZodSchema.parseAsync() -> value or error
 */
export async function getQueryValueFromURL<T>(
  queryParam: string,
  valueZodType: ZodType,
  defaultValue?: T,
  errorMessage?: string
): Promise<T> {
  const url = new URL(window.location.href);
  const urlQuery = new URLSearchParams(url.search);
  // URLSearchParams.get() will give value or null. But zod needs undefined to use z.default()
  const valueInURL = urlQuery.get(queryParam) || undefined;

  const zodParser = defaultValue
    ? valueZodType.default(defaultValue)
    : valueZodType;

  return await zodParser // Using async parse to allow zod preprocessing to get default values from a promise. (e.g., await user().email)
    .parseAsync(valueInURL)
    .catch(() => {
      // We're not throwing but returning the value.
      // When this value goes to URLParamsSyncSelector in that page, it'll make a list of all invalid URL params values and then throws to allow the error page to list all invalid params.
      return {
        error: errorMessage ?? 'Invalid param value',
      } as T;
    });
}
